import React, { useState } from 'react';
import styles from '../styles/FoodDetail.module.css';

const stars = [1, 2, 3, 4, 5];

const FoodDetail = ({
  title,
  image,
  images,
  price,
  rating,
  reviewCount,
  description,
  details,
  ingredients,
  categories,
  reviews,
  onAddToCart,
  product,
}) => {
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [tab, setTab] = useState('description');
  const gallery = images.length ? images : image ? [{ src: image, alt: title }] : [];
  const selectedImage = gallery[activeImage] || gallery[0];

  const changeQuantity = amount => {
    setQuantity(currentQuantity => Math.max(1, currentQuantity + amount));
  };

  const addToCart = () => {
    onAddToCart(product, quantity);
  };

  return (
    <section className={styles.page} aria-labelledby="food-detail-title">
      <div className={styles.content}>
        <div className={styles.gallery}>
          <h1 className={styles.detailTitle}>Food Details</h1>

          <div className={styles.mainImage}>
            {selectedImage && (
              <img
                src={selectedImage.src}
                alt={selectedImage.alt || title}
                decoding="async"
              />
            )}
          </div>

          {gallery.length > 1 && (
            <div className={styles.thumbs} aria-label="Food images">
              {gallery.map((galleryImage, index) => (
                <button
                  className={index === activeImage ? styles.thumbActive : styles.thumb}
                  key={galleryImage.src}
                  type="button"
                  onClick={() => setActiveImage(index)}
                  aria-label={`Show image ${index + 1} of ${gallery.length}`}
                  aria-pressed={index === activeImage}
                >
                  <img src={galleryImage.src} alt="" loading="lazy" decoding="async" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={styles.details}>
          <h2 id="food-detail-title">{title}</h2>

          <div className={styles.rating} aria-label={`${rating} out of 5 stars`}>
            <span className={styles.stars} aria-hidden="true">
              {stars.map(star => (
                <span className={star <= rating ? styles.starActive : styles.star} key={star}>★</span>
              ))}
            </span>
            <span>({reviewCount} customer reviews)</span>
          </div>

          <p className={styles.price}>{price}</p>

          <div className={styles.copy}>
            <p>{description}</p>
            {details && <p>{details}</p>}
          </div>

          <div className={styles.actions}>
            <div className={styles.quantity} aria-label="Quantity">
              <button type="button" onClick={() => changeQuantity(-1)} aria-label="Decrease quantity">−</button>
              <span>{quantity}</span>
              <button type="button" onClick={() => changeQuantity(1)} aria-label="Increase quantity">+</button>
            </div>
            <button className={styles.cartButton} type="button" onClick={addToCart}>
              Add to cart
            </button>
          </div>

          {categories.length > 0 && (
            <p className={styles.categories}>
              <strong>Categories:</strong> {categories.join(', ')}
            </p>
          )}
        </div>
      </div>

      <div className={styles.tabs}>
        <div className={styles.tabButtons} role="tablist" aria-label="Food information">
          <button
            className={tab === 'description' ? styles.tabActive : styles.tab}
            type="button"
            role="tab"
            aria-selected={tab === 'description'}
            onClick={() => setTab('description')}
          >
            Description
          </button>
          <button
            className={tab === 'ingredients' ? styles.tabActive : styles.tab}
            type="button"
            role="tab"
            aria-selected={tab === 'ingredients'}
            onClick={() => setTab('ingredients')}
          >
            Ingredients
          </button>
          <button
            className={tab === 'reviews' ? styles.tabActive : styles.tab}
            type="button"
            role="tab"
            aria-selected={tab === 'reviews'}
            onClick={() => setTab('reviews')}
          >
            Reviews ({reviewCount})
          </button>
        </div>

        {tab === 'description' ? (
          <div className={styles.tabContent} role="tabpanel">
            <h3>Description</h3>
            <p>{description}</p>
            {ingredients && <p><strong>Ingredients:</strong> {ingredients}</p>}
          </div>
        ) : tab === 'reviews' ? (
          <div className={styles.tabContent} role="tabpanel">
            <h3>Reviews</h3>
            {reviews.length > 0 ? reviews.map(review => (
              <article className={styles.review} key={`${review.author}-${review.text}`}>
                <strong>{review.author}</strong>
                <p>{review.text}</p>
              </article>
            )) : <p>No reviews yet.</p>}
          </div>
        ) : (
          <div className={styles.tabContent} role="tabpanel">
            <h3>Ingredients</h3>
            <p>{ingredients || 'Ingredient information is not available yet.'}</p>
          </div>
        )}
      </div>
    </section>
  );
};

FoodDetail.defaultProps = {
  title: 'Food item',
  image: '',
  images: [],
  price: '',
  rating: 5,
  reviewCount: 0,
  description: '',
  details: '',
  ingredients: '',
  categories: [],
  reviews: [],
  onAddToCart: () => {},
  product: null,
};

export default FoodDetail;
